(() => {
  const DESCRIPTION_SELECTOR = '[data-carson-product-description]';
  const CONTENT_SELECTOR = '[data-carson-product-description-content]';
  const TOGGLE_SELECTOR = '[data-carson-product-description-toggle]';
  const DESCRIPTION_IMAGE_SELECTOR = 'img[data-carson-description-image]';
  const MEDIA_SELECTOR = 'img, svg, video, iframe, table, ul, ol, blockquote';
  const SHOPIFY_IMAGE_WIDTHS = [320, 480, 600, 900, 1200];
  const COLLAPSED_TEXT = 'Show More';
  const EXPANDED_TEXT = 'Show Less'; 
  const registeredDescriptions = new WeakSet();
  const visibleDescriptions = new Set();
  const pendingDescriptions = new Set();
  let updateFrame = null;

  const isShopifyImageUrl = (source) => {
    try {
      const url = new URL(source, window.location.origin);
      return url.hostname.endsWith('shopify.com') || url.pathname.includes('/cdn/shop/files/');
    } catch (error) {
      return false;
    }
  };

  const getShopifyImageUrl = (source, width) => {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', width);
      return url.href;
    } catch (error) {
      return source;
    }
  };

  const hydrateDescriptionImage = (image) => {
    if (image.dataset.carsonDescriptionLoaded === 'true') return;

    const source = image.dataset.descriptionSrc;
    const originalSrcset = image.dataset.descriptionSrcset;
    if (!source) return;

    image.dataset.carsonDescriptionLoaded = 'true';
    image.sizes = '(max-width: 749px) calc(100vw - 30px), 1200px';

    if (isShopifyImageUrl(source)) {
      image.srcset = SHOPIFY_IMAGE_WIDTHS.map((width) => `${getShopifyImageUrl(source, width)} ${width}w`).join(', ');
      image.src = getShopifyImageUrl(source, 600);
    } else {
      if (originalSrcset) image.srcset = originalSrcset;
      image.src = source;
    }

    image.removeAttribute('data-description-src');
    image.removeAttribute('data-description-srcset');
  };

  const descriptionImageObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          descriptionImageObserver.unobserve(target);
          hydrateDescriptionImage(target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  const initializeDescriptionImages = (description) => {
    description.querySelectorAll(DESCRIPTION_IMAGE_SELECTOR).forEach((image) => {
      if (descriptionImageObserver) descriptionImageObserver.observe(image);
      else hydrateDescriptionImage(image);
    });
  };

  const scheduleDescription = (description) => {
    pendingDescriptions.add(description);
    if (updateFrame !== null) return;
    updateFrame = window.requestAnimationFrame(() => {
      updateFrame = null;
      pendingDescriptions.forEach((item) => {
        if (item.isConnected) prepareDescription(item);
      });
      pendingDescriptions.clear();
    });
  };

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            visibleDescriptions.add(target);
            scheduleDescription(target);
          } else {
            visibleDescriptions.delete(target);
          }
        });
      }, { rootMargin: '300px 0px' })
    : null;

  const getNormalizedText = (element) => element.textContent.replace(/\u00a0/g, ' ').trim();

  const hasRenderableContent = (content) => getNormalizedText(content).length > 0 || content.querySelector(MEDIA_SELECTOR);

  const isEmptyEdgeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim() === '';
    if (!(node instanceof HTMLElement)) return false;
    if (node.matches(MEDIA_SELECTOR)) return false;

    return node.matches('p, div, span, br') && getNormalizedText(node).length === 0 && !node.querySelector(MEDIA_SELECTOR);
  };

  const trimEmptyEdges = (content) => {
    while (content.firstChild && isEmptyEdgeNode(content.firstChild)) {
      content.firstChild.remove();
    }

    while (content.lastChild && isEmptyEdgeNode(content.lastChild)) {
      content.lastChild.remove();
    }
  };

  const toPixels = (value, context) => {
    const numericValue = Number.parseFloat(value);

    if (Number.isNaN(numericValue)) return 104;
    if (value.includes('rem')) {
      return numericValue * Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    }
    if (value.includes('em')) {
      return numericValue * Number.parseFloat(window.getComputedStyle(context).fontSize);
    }

    return numericValue;
  };

  const getCollapsedHeight = (description) => {
    const value = window
      .getComputedStyle(description)
      .getPropertyValue('--carson-description-collapsed-height')
      .trim();

    return toPixels(value, description);
  };

  const setToggleState = (description, expanded) => {
    const button = description.querySelector(TOGGLE_SELECTOR);
    const content = description.querySelector(CONTENT_SELECTOR);

    if (!button || !content) return;

    button.textContent = expanded ? EXPANDED_TEXT : COLLAPSED_TEXT;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const expandDescription = (description) => {
    const content = description.querySelector(CONTENT_SELECTOR);
    if (!content) return;

    const startHeight = content.getBoundingClientRect().height;
    content.style.height = `${startHeight}px`;
    description.classList.remove('is-collapsed');
    description.classList.add('is-expanded');
    setToggleState(description, true);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      content.style.height = 'auto';
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;

      content.removeEventListener('transitionend', onTransitionEnd);
      if (description.classList.contains('is-expanded')) content.style.height = 'auto';
    };

    content.addEventListener('transitionend', onTransitionEnd);
    window.requestAnimationFrame(() => {
      content.style.height = `${content.scrollHeight}px`;
    });
  };

  const collapseDescription = (description) => {
    const content = description.querySelector(CONTENT_SELECTOR);
    if (!content) return;

    content.style.height = `${content.getBoundingClientRect().height}px`;
    void content.offsetHeight;
    description.classList.remove('is-expanded');
    description.classList.add('is-collapsed');
    content.style.height = `${getCollapsedHeight(description)}px`;
    setToggleState(description, false);
  };

  const bindToggle = (description) => {
    const button = description.querySelector(TOGGLE_SELECTOR);
    if (!button || button.dataset.carsonProductDescriptionBound === 'true') return;

    button.dataset.carsonProductDescriptionBound = 'true';
    button.addEventListener('click', () => {
      if (description.classList.contains('is-expanded')) {
        collapseDescription(description);
      } else {
        expandDescription(description);
      }
    });
  };

  const prepareDescription = (description) => {
    const content = description.querySelector(CONTENT_SELECTOR);
    const button = description.querySelector(TOGGLE_SELECTOR);

    if (!content || !button) return;

    if (!description.dataset.descriptionReady) {
      description.hidden = false;
      trimEmptyEdges(content);
    }

    if (!hasRenderableContent(content)) {
      description.hidden = true;
      return;
    }

    const wasExpanded = description.classList.contains('is-expanded');
    const collapsedHeight = getCollapsedHeight(description);

    // scrollHeight includes clipped content; no expansion/reflow is needed to measure it.
    const fullHeight = content.scrollHeight;
    const shouldCollapse = fullHeight > collapsedHeight + 1;

    if (!shouldCollapse) {
      description.classList.remove('is-collapsible', 'is-collapsed', 'is-expanded');
      content.style.height = '';
      button.hidden = true;
      setToggleState(description, false);
      description.dataset.descriptionReady = 'true';
      return;
    }

    description.classList.add('is-collapsible');
    description.dataset.descriptionReady = 'true';
    button.hidden = false;
    bindToggle(description);

    if (wasExpanded) {
      description.classList.remove('is-collapsed');
      description.classList.add('is-expanded');
      content.style.height = 'auto';
      setToggleState(description, true);
      return;
    }

    description.classList.add('is-collapsed');
    content.style.height = `${collapsedHeight}px`;
    setToggleState(description, false);
  };

  const initializeDescriptions = (root = document) => {
    const descriptions = root.matches?.(DESCRIPTION_SELECTOR) ? [root] : root.querySelectorAll(DESCRIPTION_SELECTOR);

    descriptions.forEach((description) => {
      if (registeredDescriptions.has(description)) return;
      registeredDescriptions.add(description);
      initializeDescriptionImages(description);
      description.addEventListener('load', () => {
        if (visibleDescriptions.has(description)) scheduleDescription(description);
      }, true);
      if (visibilityObserver) {
        visibilityObserver.observe(description);
      } else {
        visibleDescriptions.add(description);
        scheduleDescription(description);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeDescriptions());
  } else {
    initializeDescriptions();
  }

  const refreshVisibleDescriptions = () => {
    visibleDescriptions.forEach((description) => {
      if (description.isConnected) scheduleDescription(description);
      else {
        visibleDescriptions.delete(description);
        visibilityObserver?.unobserve(description);
      }
    });
  };
  window.addEventListener('resize', refreshVisibleDescriptions, { passive: true });
  document.fonts?.ready.then(refreshVisibleDescriptions);
  document.addEventListener('product-info:loaded', (event) => initializeDescriptions(event.target));
  document.addEventListener('shopify:section:load', (event) => initializeDescriptions(event.target));
})();
